import React from "react";
import { Link } from "react-router-dom";

const FEATURES = [
  {
    n: "01",
    label: "Market visibility",
    title: "Referral Market Radar",
    body:
      "A live map of every referring provider in your market — 2.4 million nationwide — scored by real activity. See exactly where the referrals are, who is sending them, and which parts of your area you haven't reached.",
  },
  {
    n: "02",
    label: "Payer strategy",
    title: "Private Payer Intelligence",
    body:
      "Every provider carries a Private Payer Score built from Census insurance data and CMS billing patterns — so your outreach optimizes the payer mix for your practice, not just its volume.",
  },
  {
    n: "03",
    label: "Daily practice",
    title: "Referral CRM",
    body:
      "Log your referrers, tally each referral in seconds — payer, appropriateness, surgical conversion — plan your outreach calls, and watch your penetrance grow ZIP by ZIP.",
  },
  {
    n: "04",
    label: "By design",
    title: "Zero Patient Data",
    body:
      "Built de-identified from the ground up: no names, no charts, no PHI — only month-level, area-level referral tallies. Nothing to breach, nothing to audit, nothing slowing you down.",
  },
];

export default function CompanyHome() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero — split: content left, photo right, cream fading into the image */}
      <section style={{ backgroundColor: "#FDF9F5" }} className="relative overflow-hidden">
        <div className="hidden lg:block absolute inset-y-0 right-0 w-[46%]">
          <img
            src="/hero-doctor-full.jpg"
            alt=""
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 25%" }}
          />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to right, #FDF9F5 0%, rgba(253,249,245,0.55) 28%, rgba(253,249,245,0) 60%)" }}
          />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-20 lg:grid lg:grid-cols-2 lg:items-center">
          <div className="text-center lg:text-left">
          <img
            src="/PlexysMark.png"
            alt="Plexys"
            className="w-56 md:w-72 mx-auto lg:mx-0 mb-6"
          />
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-4 tracking-tight">
            Plexys
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-3">
            Referral intelligence for the modern practice.
          </p>
          <p className="max-w-2xl mx-auto lg:mx-0 text-gray-500 mb-10">
            Know who refers to you, who should, and where your next referral is
            coming from — without touching a single piece of patient data.
          </p>
          <div className="flex flex-wrap justify-center lg:justify-start gap-3">
            <Link
              to="/refer/register"
              className="px-8 py-3 bg-green-800 text-white rounded-xl font-semibold text-lg hover:bg-green-900 transition"
            >
              Start free — 7 days
            </Link>
            <Link
              to="/refer/login"
              className="px-8 py-3 border-2 border-green-800 text-green-800 rounded-xl font-semibold text-lg hover:bg-white transition"
            >
              Log in
            </Link>
            <a
              href="/download/Plexys-mac.dmg"
              className="px-8 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold text-lg hover:bg-white transition"
            >
              ↓ Mac
            </a>
            <a
              href="/download/Plexys-win.exe"
              className="px-8 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold text-lg hover:bg-white transition"
            >
              ↓ Windows
            </a>
          </div>
          <p className="mt-4 text-sm text-gray-400">
            Free for 7 days, then $49/month. No credit card to start. Cancel anytime.
          </p>
          <p className="mt-2 text-sm text-gray-400">
            Built for the physicians patients are referred to — surgeons and specialists who live on referrals.
          </p>
          <div className="mt-10 lg:hidden max-w-3xl mx-auto">
            <img
              src="/hero-doctor.jpg"
              alt="A physician in consultation with a patient"
              className="w-full rounded-2xl shadow-xl"
              loading="eager"
            />
          </div>
          </div>
        </div>
      </section>

      {/* Features — editorial list, no cards */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Everything a referral practice runs on
        </h2>
        <p className="text-gray-500 mb-12">
          One tool for the question every practice asks: where does my next
          referral come from?
        </p>
        <div className="divide-y divide-gray-200">
          {FEATURES.map((f) => (
            <div key={f.n} className="py-10 grid md:grid-cols-12 gap-4 md:gap-8">
              <div className="md:col-span-3 flex md:block items-baseline gap-3">
                <span className="text-sm font-mono text-green-800">{f.n}</span>
                <div className="text-xs uppercase tracking-widest text-gray-400 md:mt-2">
                  {f.label}
                </div>
              </div>
              <div className="md:col-span-9">
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                  {f.title}
                </h3>
                <p className="text-gray-600 leading-relaxed max-w-2xl">{f.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* See it in action */}
      <section className="bg-gray-900 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white mb-2 text-center">See it in action</h2>
          <p className="text-gray-400 text-center mb-12">Real screens from the product, running on the national provider dataset.</p>
          <div className="space-y-12">
            {[
              ["/shots/radar.jpg", "The Market Radar"],
              ["/shots/zip-panel.jpg", "Click a ZIP, see the whole market"],
              ["/shots/crm.jpg", "Your referral CRM"],
              ["/shots/dashboard.jpg", "Your practice at a glance"],
            ].map(([src, title]) => (
              <figure key={src} className="max-w-3xl mx-auto">
                <div className="rounded-xl overflow-hidden border border-gray-700 shadow-2xl">
                  <div className="h-7 bg-gray-800 flex items-center gap-1.5 px-3">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  </div>
                  <img src={src} alt={title} className="w-full block" loading="lazy" />
                </div>
                <figcaption className="mt-3 text-center">
                  <span className="text-white font-semibold">{title}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section style={{ backgroundColor: "#FDF9F5" }} className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Three minutes a day
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              ["Log your clinic day", "Each referral is four taps: who sent it, the payer, how appropriate, surgical or not."],
              ["See your market", "The radar shows your penetrance, your gaps, and the high-value providers you haven't met."],
              ["Work the list", "One click puts a provider on your call list — with the context you need when you dial."],
            ].map(([title, body], i) => (
              <div key={title}>
                <div className="text-sm font-mono text-green-800 mb-2">{`0${i + 1}`}</div>
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link
              to="/refer/register"
              className="inline-block mt-12 px-8 py-3 bg-green-800 text-white rounded-xl font-semibold hover:bg-green-900 transition"
            >
              Try it on your next clinic day →
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-8">
          <div>
            <h4 className="text-white font-bold text-lg mb-2">Plexys</h4>
            <p className="text-sm text-gray-400">
              Referral intelligence for the modern practice.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-3">Support</h4>
            <a href="mailto:support@plexys.co" className="text-sm hover:text-white transition">
              support@plexys.co
            </a>
          </div>
          <div>
            <h4 className="font-semibold mb-3">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/privacy-policy" className="hover:text-white transition">Privacy Policy</Link></li>
              <li><Link to="/terms-of-service" className="hover:text-white transition">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 mt-8 pt-6 border-t border-gray-800 text-sm text-gray-500">
          © {new Date().getFullYear()} plexys limited. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
